<template>
  <modal
    v-if="show"
    :title="i18n.t('/KlantZoekenModal/ZoekNaarKlanten')"
    class="modal"
    @close="close"
  >
    <loader v-if="loading" size="large" />

    <template v-else>
      <form-toggle
        v-if="showCommonDriversToggle"
        id="showCommonDrivers"
        v-model="showCommonDrivers"
        :title="commonDriversLabel"
      />

      <dealer-customer-search
        v-if="step < 2"
        @has-searched-dealer-customers="onSearchedDealerCustomers"
        @has-searched-by-licence-plate="onSearchedByLicencePlate"
      />

      <national-customer-search
        v-else
        @has-search-national-customers="onSearchedNationalCustomers"
      />

      <div v-if="vehicleInformationExtended.ModelName" class="car-details">
        <car-details :details="vehicleInformationExtended" :read-only="true" />
      </div>

      <search-customer-results
        v-if="showCommonDrivers"
        :customers="commonDrivers"
        :is-purchase-order="pageType === 'purchaseOrder'"
        @select-customer="selectCustomer"
      />

      <search-customer-results
        v-else
        :customers="foundCustomers"
        :has-searched="hasSearchedDealerCustomers || hasSearchedNationalCustomers"
        :was-search-by-licence-plate="searchedByLicensePlate"
        :is-purchase-order="pageType === 'purchaseOrder'"
        @select-customer="selectCustomer"
        @select-vehicle-customer-to-change="selectVehicleCustomerToChange"
      />

      <app-button
        v-if="step === 1"
        data-qa="search-customers-national-button"
        @click="searchNational"
      >
        {{ i18n.t('/KlantZoekenModal/ZoekNationaal') }}
      </app-button>

      <edit-customer
        v-if="step > 3 && !disableAddCustomer"
        :customer="{}"
        button-title="Nieuwe klant"
        :modal-title="
          i18n.t('/customer/create new customer form/new customer button and form title')
        "
        @update-customer="onNewCustomer"
      />
    </template>

    <modal
      v-if="showSelectVehicleCustomerSearch"
      :title="modalSelectVehicleCustomerTitle"
      @close="closeSelectVehicleCustomer"
    >
      <vehicle-customer-search @has-searched-vehicle-customers="onSearchedVehicleCustomers" />
      <search-customer-results
        :customers="foundCustomersForVehicle"
        :has-searched="hasSearchedVehicleCustomers"
        :is-purchase-order="pageType === 'purchaseOrder'"
        @select-customer="selectVehicleCustomer"
      />
    </modal>
  </modal>
</template>

<script>
import FormToggle from '@/components/generic/presentational/atoms/formCheckbox/FormToggle.vue'
import SearchCustomerResults from '@/components/generic/presentational/organisms/searchCustomerModal/SearchCustomerResults.vue'
import StoreConnector from '@/helpers/StoreConnector'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import DealerCustomerSearch from '@/components/generic/presentational/molecules/customerSearch/DealerCustomerSearch.vue'
import VehicleCustomerSearch from '@/components/generic/presentational/molecules/customerSearch/VehicleCustomerSearch.vue'
import NationalCustomerSearch from '@/components/generic/presentational/molecules/customerSearch/NationalCustomerSearch.vue'
import { EventBus } from '@/plugins/event-bus'
import EditCustomer from '@/components/purchaseOrder/presentational/organisms/newCustomerSearch/EditCustomer.vue'
import CustomersApi from '@/api/server/Customers'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import CustomerSearchHelper from '@/helpers/CustomerSearchHelper'
import CarDetails from '@/components/generic/presentational/molecules/carDetails/CarDetails.vue'
import { mapGetters } from 'vuex'
import { pageTypes } from '@/helpers/pageTypeConstants'

export default {
  components: {
    AppButton,
    Modal,
    DealerCustomerSearch,
    VehicleCustomerSearch,
    NationalCustomerSearch,
    SearchCustomerResults,
    FormToggle,
    EditCustomer,
    Loader,
    CarDetails,
  },
  provide() {
    return {
      isNationalCustomerSearch: this.isNationalCustomerSearch,
    }
  },
  props: {
    translations: {
      type: Object,
      default: () => {},
    },
    disableAddCustomer: {
      type: Boolean,
      default: false,
    },
    show: {
      type: Boolean,
      default: false,
    },
    showCommonDriversCheckbox: {
      type: Boolean,
      default: false,
    },
    isPurchaseOrderCustomerSearch: {
      type: Boolean,
      default: false,
    },
    pageType: {
      type: String,
      default: pageTypes.PURCHASE_ORDER,
      validator(value) {
        return Object.values(pageTypes).includes(value)
      },
    },
  },
  emits: ['close', 'createCustomer', 'selectCustomer'],
  data() {
    return {
      foundCustomers: [],
      foundCustomersForVehicle: [],
      amountToShow: 20,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      step: 0,
      hasSearchedDealerCustomers: false,
      hasSearchedNationalCustomers: false,
      hasSearchedVehicleCustomers: false,
      hasCompany: false,
      showCommonDrivers: false,
      recommendedCustomers: [],
      company: {},
      newCustomerModal: false,
      loading: false,
      searchedByLicensePlate: false,
      vehicleRelation: '',
      showSelectVehicleCustomerSearch: false,
      vehicleInformation: {},
      vehicleInformationExtended: {},
    }
  },
  computed: {
    ...mapGetters({ dealerSettings: 'user/dealerSettings' }),
    commonDriversLabel() {
      return 'Standaard contacten ' + this.companyName
    },
    companyName() {
      return this.company.CustomerCompanyName
    },
    showNoResultsMessage() {
      return (
        this.foundCustomers.length == 0 &&
        (this.hasSearchedDealerCustomers || this.hasSearchedNationalCustomers)
      )
    },
    customersToShow() {
      return this.foundCustomers.filter((customer, index) => index < this.amountToShow)
    },
    showCommonDriversToggle() {
      return this.hasCompany && this.showCommonDriversCheckbox
    },
    commonDrivers() {
      let director = Object.assign({}, this.commonDriverBase)
      director.CustomerFamilyName = 'Directeur ' + this.companyName

      let wagenparkBeheerder = Object.assign({}, this.commonDriverBase)
      wagenparkBeheerder.CustomerFamilyName = 'Wagenparkbeheerder ' + this.companyName

      let divBestuurder = Object.assign({}, this.commonDriverBase)
      divBestuurder.CustomerFamilyName = 'Div bestuurders ' + this.companyName

      let bestuurderOnbekend = Object.assign({}, this.commonDriverBase)
      bestuurderOnbekend.CustomerFamilyName = 'Bestuurder onbekend ' + this.companyName

      let commonDrivers = []

      // filter existing drivers
      ;[director, wagenparkBeheerder, bestuurderOnbekend, divBestuurder].forEach((commonDriver) => {
        if (
          !this.recommendedCustomers.some(
            (existingDriver) => existingDriver.CustomerFamilyName == commonDriver.CustomerFamilyName
          )
        ) {
          commonDrivers.push(commonDriver)
        }
      })

      return commonDrivers
    },
    modalSelectVehicleCustomerTitle() {
      return (
        'Zoek naar voertuig ' +
        this.i18n.t(`/klantzoekenmodal/licenseplaterelation/${this.vehicleRelation}`)?.toLowerCase()
      )
    },
    commonDriverBase() {
      const parsedAddress = CustomerSearchHelper.parseAddress(this.company.CustomerCompanyAddress)
      return {
        CustomerAddress: this.company.CustomerCompanyAddress,
        CustomerCellPhone: this.company.CustomerCompanyPhoneNumber,
        CustomerCity: this.company.CustomerCompanyCity,
        CustomerCompanyEmail: this.company.CustomerCompanyEmailAddress,
        CustomerCompanyMagicNumber: this.company.CustomerCompanyMagicNumber,
        CustomerCompanyPhone: this.company.CustomerCompanyPhoneNumber,
        CustomerCountry: null,
        CustomerCountryCode: 'NL',
        CustomerDateOfBirth: null,
        CustomerEmail: this.company.CustomerCompanyEmailAddress,
        CustomerFamilyName: this.companyName,
        CustomerFirstName: '',
        CustomerGender: 'UNKNOWN',
        CustomerHasCmdmFailed: null,
        CustomerHasCmdmFailedValue: false,
        CustomerHouseNumber: parsedAddress.houseNumber,
        CustomerHouseNumberAddition: parsedAddress.houseNumberAddition,
        CustomerInitials: '',
        CustomerMagicNumber: null,
        CustomerMiddleName: '',
        CustomerPhone: this.company.CustomerCompanyPhoneNumber,
        CustomerPrivateEmail: '',
        CustomerPrivatePhone: '',
        CustomerRemark: null,
        CustomerSource: 'CMDM',
        CustomerStreet: parsedAddress.street,
        CustomerTitle: '',
        CustomerUpdateToAutoline: true,
        CustomerZipCode: this.company.CustomerCompanyZipcode,
        IsKnownInCmdm: true,
        KnownCustomer: false,
        PoId: null,
      }
    },
  },

  created() {
    StoreConnector.get('customer/customersSearchResults', (data) => {
      this.foundCustomers = data
    })

    StoreConnector.get('customer/vehicleCustomersSearchResult', (data) => {
      this.foundCustomersForVehicle = data
    })

    StoreConnector.get('customer/recommendedCustomers', (data) => {
      this.recommendedCustomers = data
    })

    StoreConnector.get('customer/customerVehicleInformation', (data) => {
      this.vehicleInformation = data
    })

    StoreConnector.get('customer/customerVehicleInformationExtended', (data) => {
      this.vehicleInformationExtended = data
    })

    // We use the customer search in Purchase Order and on the Quote Dashboard
    // In order to use this component from both places without much refactoring
    // a switch was added:
    if (!this.isPurchaseOrderCustomerSearch) {
      StoreConnector.get('quote/hasCustomerCompany', (data) => {
        this.hasCompany = data
      })

      StoreConnector.get('quote/customerCompany', (data) => {
        this.company = data
      })
    } else {
      StoreConnector.get('purchaseOrder/hasCustomerCompany', (data) => {
        this.hasCompany = data
      })

      StoreConnector.get('purchaseOrder/customerCompany', (data) => {
        this.company = data
      })
    }
    if (!this.dealerSettings.autolineKeyloopEnabled) {
      this.searchNational()
    } else {
      this.step = 0
    }
  },
  methods: {
    close() {
      this.$emit('close')
      if (!this.dealerSettings.autolineKeyloopEnabled) {
        this.searchNational()
      } else {
        this.step = 0
      }
      this.hasSearchedDealerCustomers = false
      this.hasSearchedNationalCustomers = false
      StoreConnector.dispatch('customer/resetSearchResults')
      this.amountToShow = 20
    },
    async onSearchedByLicencePlate(searchedByLicensePlate) {
      this.searchedByLicensePlate = searchedByLicensePlate
    },
    async onSearchedDealerCustomers() {
      this.step = 1
      this.hasSearchedDealerCustomers = true
      this.amountToShow = 20
    },
    searchNational() {
      this.step = 3
      this.hasSearchedDealerCustomers = false
      StoreConnector.dispatch('customer/resetSearchResults')
    },
    onSearchedNationalCustomers() {
      this.step = 4
      this.hasSearchedNationalCustomers = true
    },
    onSearchedVehicleCustomers() {
      this.hasSearchedVehicleCustomers = true
    },
    addCustomer() {
      EventBus.$emit('addCustomer')
      this.close()
    },
    showMoreResults() {
      this.amountToShow += 20
    },
    selectCustomer(customer) {
      if (this.showCommonDrivers) {
        // common driver selected, create a new customer.
        this.$emit('createCustomer', customer)
      } else {
        this.$emit('selectCustomer', customer)
      }

      this.close()
    },
    async selectVehicleCustomer(customer) {
      await CustomersApi.postCustomerVehicleRelation({
        customerMagicNumber: customer.CustomerMagicNumber,
        vehicleId: this.vehicleInformation.vehicleId, //todo get id from license plate
        relationType: this.vehicleRelation,
      })

      this.selectCustomer(customer)
    },
    selectVehicleCustomerToChange(customer) {
      this.vehicleRelation = customer.CustomerRelation
      StoreConnector.dispatch('customer/setSearchQuery', '')
      this.showSelectVehicleCustomerSearch = true
    },
    closeSelectVehicleCustomer() {
      this.showSelectVehicleCustomerSearch = false
    },
    async onNewCustomer(customer) {
      if (!this.isPurchaseOrderCustomerSearch) {
        await this.createAndSetNewCustomerOnQuote(customer)
      } else {
        await this.createAndSetNewCustomerOnPurchaseOrder(customer)
      }
    },
    async createAndSetNewCustomerOnQuote(customer) {
      this.loading = true

      const createdCustomer = await CustomersApi.createAccount(customer)
      StoreConnector.dispatch('quote/setCustomer', createdCustomer)
        .then(() => {
          this.close()

          StoreConnector.dispatch('quote/createOrUpdate', createdCustomer).then(() => {
            EventBus.$emit('quote/customerUpdated')
          })
        })
        .finally(() => (this.loading = false))
    },
    async createAndSetNewCustomerOnPurchaseOrder(customer) {
      this.loading = true

      const createdCustomer = await CustomersApi.createAccount(customer)
      StoreConnector.dispatch('purchaseOrder/setCustomer', createdCustomer)
        .then(() => {
          this.close()
        })
        .finally(() => (this.loading = false))
    },
    isNationalCustomerSearch() {
      return this.hasSearchedNationalCustomers
    },
  },
}
</script>

<style lang="scss" scoped>
.search-customer {
  margin-bottom: 1em;
}

ul {
  @include list-reset;
}

.car-details {
  position: relative;
  background-color: $white;
  border: solid 1px $gray-base;
  border-radius: 0.25em;
  margin-bottom: 1rem;
  padding: 1em;
  min-width: 25em;
  // top: 2.5em;
}
</style>
